import { useSelector } from 'react-redux';
import { Box, Button, Chip, InputBase, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { VOLUME_UOMS, WEIGHT_UOMS } from '../../../lib/packingListConstants';
import { compareTotals } from '../../../lib/packingListValidation';

const VERDICT_COLOR = { ok: 'success', mismatch: 'warning', unit_mismatch: 'warning', skipped: 'default' };

/** 문서 합계의 단위 칸. 순중량·총중량은 문서 중량 단위(weight_uom) 하나를 함께 쓰므로 어느 행에서 바꿔도 둘 다 바뀐다. */
const UNIT_PICKERS = { qty: 'qty_uom', net_weight: 'weight_uom', gross_weight: 'weight_uom', volume: 'volume_uom' };

/** 표 칸 안에 테두리 없이 들어가는 입력. 마우스를 올리거나 고칠 때만 칸이 드러난다. */
const inlineSx = (error) => ({
    width: '100%',
    height: 24,
    fontSize: 11,
    borderRadius: '4px',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
    '&.Mui-focused': { bgcolor: 'white', outline: '1px solid #00647e' },
    ...(error && { bgcolor: 'rgba(217,41,54,0.12)', outline: '1px solid #d92936' }),
});

function UnitPicker({ label, value, options, onChange, readOnly }) {
    return (
        <TextField
            select
            variant="standard"
            fullWidth
            value={value ?? options[0]}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ 'aria-label': label }}
            InputProps={{ disableUnderline: true, readOnly, sx: inlineSx(false) }}
            SelectProps={{ MenuProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, slotProps: { paper: { sx: { maxHeight: 180 } } } } }}
            sx={{ '& .MuiSelect-select': { fontSize: 11, py: 0, pl: 1, color: '#003a52' } }}
        >
            {options.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 11 }}>{o}</MenuItem>)}
        </TextField>
    );
}

/**
 * 문서 총합과 라인 합산을 수량·카톤 수·순중량·총중량·부피, 그리고 합계행에만 있는 단위(팔레트 등) 순으로 나란히 둔다. 환산 없음. 판정 규칙은 서버 확정 검증과 같다.
 * 보조 단위 행의 Document 칸은 totals.quantities를 고친다 — 파서가 팔레트 수를 잘못 읽었을 때 검수자가 바로잡는 길이다.
 * 문서 합계는 Document 칸, 단위는 Unit 칸에서 따로 고친다. 어긋난 행은 "Apply line sum"으로 라인 합계를 그대로 옮길 수도 있다.
 * readOnly면 합계·단위를 고칠 수 없고 Apply line sum도 없다.
 */
export default function TotalCompareTable({ header, lines, totals = null, onChange, onTotalsChange, highlightField, readOnly = false }) {
    const uomMaster = useSelector((s) => s.packingList.uomMaster);
    const unitOptions = { qty_uom: [...new Set([header.qty_uom, ...uomMaster].filter(Boolean))], weight_uom: WEIGHT_UOMS, volume_uom: VOLUME_UOMS };
    const rows = compareTotals(header, lines, totals);
    /** 헤더 칸이 있는 행은 헤더를, 합계행 전용 단위(팔레트 등)는 totals.quantities를 고친다. */
    const fieldOf = (r) => r.headerField ?? r.field;
    const docValue = (r) => (r.headerField ? header[r.headerField] : r.doc);
    const setDoc = (r, value) => (r.headerField ? onChange(r.headerField, value) : onTotalsChange?.(r.totalsUnit, value));
    const mixed = rows.find((r) => r.key === 'qty')?.mixed;
    const fmt = (n) => (n === null || n === undefined ? '—' : Number(n.toFixed(3)).toLocaleString());
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#003a52' }}>Document totals vs line sums</Typography>
                {mixed && <Chip size="small" color="warning" variant="outlined" label={rows[0].verdictLabel} sx={{ fontSize: 10, height: 18 }} />}
            </Box>
            <Table size="small" sx={{ '& th': { bgcolor: '#f1f1f1', fontSize: 11, fontWeight: 500, py: 0.5 }, '& td': { fontSize: 11, py: 0.5 } }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Line sum</TableCell>
                        <TableCell align="center" colSpan={2}>Document</TableCell>
                        <TableCell align="right">Diff</TableCell>
                        <TableCell>Verdict</TableCell>
                        <TableCell align="center">Sync</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.key} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{r.label}</TableCell>
                            <TableCell align="right">{fmt(r.line)} {r.unit ?? ''}</TableCell>
                            <TableCell align="right" sx={{ width: 120 }}>
                                <InputBase
                                    id={`header-${fieldOf(r)}`}
                                    type="number"
                                    value={docValue(r) ?? ''}
                                    onChange={(e) => setDoc(r, e.target.value === '' ? null : Number(e.target.value))}
                                    placeholder="—"
                                    readOnly={readOnly}
                                    inputProps={{ 'aria-label': `Document ${r.label}`, style: { textAlign: 'right', padding: '0 8px' } }}
                                    sx={inlineSx(highlightField === fieldOf(r))}
                                />
                            </TableCell>
                            <TableCell sx={{ width: 90 }}>
                                {UNIT_PICKERS[r.key] ? (
                                    <UnitPicker label={`Document ${r.label} unit`} value={header[UNIT_PICKERS[r.key]]} options={unitOptions[UNIT_PICKERS[r.key]]} onChange={(v) => onChange(UNIT_PICKERS[r.key], v)} readOnly={readOnly} />
                                ) : (
                                    <Typography sx={{ fontSize: 11, color: '#003a52', px: 1 }}>{r.unit ?? ''}</Typography>
                                )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{r.diff === null ? '—' : r.diff > 0 ? `+${fmt(r.diff)}` : fmt(r.diff)}</TableCell>
                            <TableCell><Chip size="small" color={VERDICT_COLOR[r.verdict]} variant={r.verdict === 'ok' ? 'filled' : 'outlined'} label={r.verdictLabel} sx={{ fontSize: 10, height: 18 }} /></TableCell>
                            <TableCell align="center">
                                {r.verdict === 'mismatch' && r.line > 0 && !readOnly ? (
                                    <Button size="small" variant="outlined" onClick={() => setDoc(r, Number(r.line.toFixed(3)))} sx={{ fontSize: 10, py: 0, minWidth: 0 }}>Apply line sum</Button>
                                ) : (
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>—</Typography>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
}
